import {AnswerType, QuestionType} from "../../definitions/types";
import {AnswerModel} from "./answerModel";
import {getTimeAgo} from "../../utils/helpers";

export class QuestionModel {
    private readonly _accountId?: number;
    private readonly _answers: AnswerModel[];
    private readonly _created: Date;
    private readonly _id: number;
    private readonly _name: string;
    private readonly _productId: number;
    private readonly _text: string;
    private readonly _updated?: Date;
    private readonly _visible: boolean;

    constructor(questionType: QuestionType) {
        this._accountId = questionType.accountId;
        this._answers = questionType.answers
            .filter((answer): answer is AnswerType => !!(answer !== null && answer.name && answer.text))
            .map(answer => new AnswerModel(answer));
        this._created = questionType.created;
        this._id = questionType.id;
        this._name = questionType.name;
        this._productId = questionType.productId;
        this._text = questionType.text;
        this._updated = questionType.updated;
        this._visible = questionType.visible;
    }

    /**
     * @method accountId
     * @description Gets the account ID for the question.
     * @returns {number | undefined}
     */
    public get accountId(): number | undefined {
        return this._accountId;
    }

    /**
     * @method answers
     * @description Gets the answers for the question.
     * @returns {AnswerModel[]}
     */
    public get answers(): AnswerModel[] {
        return this._answers;
    }

    /**
     * @method answerCount
     * @description Gets the number of answers for the question.
     * @returns {number}
     */
    public get answerCount(): number {
        return this._answers.length;
    }

    /**
     * @method hasAnswers
     * @description Gets the flag indicating if the question has answers.
     * @returns {boolean}
     */
    public get hasAnswers(): boolean {
        return this.answerCount > 0;
    }

    /**
     * @method doesNotHaveAnswers
     * @description Gets the flag indicating if the question does not have answers.
     * @returns {boolean}
     */
    public get doesNotHaveAnswers(): boolean {
        return !this.hasAnswers;
    }

    /**
     * @method formattedAnsweredCount
     * @description Gets the formatted number of answers for the question.
     * @returns {string}
     */
    public get formattedAnsweredCount(): string {
        return this.hasAnswers ? `${this.answerCount} Answer${this.answerCount !== 1 ? 's' : ''}` : 'No Answers';
    }

    /**
     * @method created
     * @description Gets the created date for the question.
     * @returns {Date}
     */
    public get created(): Date {
        return this._created;
    }

    /**
     * @method createdOn
     * @description Gets the created date for the answer in a readable format.
     * @returns {string}
     */
    public get createdOn(): string {
        return this._created.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * @method byline
     * @description Gets the byline for the question.
     * @returns {string}
     */
    public get byline(): string {
        return `${this.createdAgo} by ${this._name}`;
    }

    /**
     * @method createdAgo
     * @description Gets the created date for the answer in a readable format.
     * @returns {string}
     */
    public get createdAgo(): string {
        return getTimeAgo(this._created);
    }

    /**
     * @method id
     * @description Gets the ID for the question.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @method name
     * @description Gets the name for the question.
     * @returns {string}
     */
    public get name(): string {
        return this._name;
    }

    /**
     * @method productId
     * @description Gets the product ID for the question.
     * @returns {number}
     */
    public get productId(): number {
        return this._productId;
    }

    /**
     * @method text
     * @description Gets the text for the question.
     * @returns {string}
     */
    public get text(): string {
        return this._text;
    }

    /**
     * @method updated
     * @description Gets the updated date for the question.
     * @returns {Date | undefined}
     */
    public get updated(): Date | undefined {
        return this._updated;
    }

    /**
     * @method updatedOn
     * @description Gets the updated date for the answer in a human readable format.
     * @returns {string | undefined}
     */
    public get updatedOn(): string | undefined {
        return this._updated
            ? this._updated.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })
            : undefined;
    }

    /**
     * @method visible
     * @description Gets the visible flag for the question.
     * @returns {boolean}
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * @method href
     * @description Gets the href for the question.
     * @returns {string}
     */
    public get href(): string {
        return `/QA/Question/${this._id}`;
    }
}